/**
 * Integrytas Base App · MOBILE primitives — the card family (Direction C).
 *   MobileContentCard  the elevated rounded-14 card (list or generic)
 *   MobileCardRow      a 70px list row (lead · title/subtitle · trailing)
 *   MobileDetailRow    a 46px key/value row for detail cards (C05)
 */
import type { ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';

// ── Content card ────────────────────────────────────────────────────────────
interface MobileContentCardProps {
  children: ReactNode;
  /** Wrap children in a hairline-separated .m-list (for MobileCardRow lists). */
  list?: boolean;
  className?: string;
}

export function MobileContentCard({ children, list = false, className = '' }: MobileContentCardProps) {
  return (
    <div className={'m-card' + (className ? ' ' + className : '')}>
      {list ? <div className="m-list">{children}</div> : children}
    </div>
  );
}

// ── Card row (70px) ─────────────────────────────────────────────────────────
export type MobileLeadTone = 'blue' | 'green' | 'amber' | 'red' | 'slate';

const LEAD_TONE: Record<MobileLeadTone, string> = {
  blue: 'm-l-blue', green: 'm-l-green', amber: 'm-l-amber', red: 'm-l-red', slate: 'm-l-slate',
};

interface MobileCardRowProps {
  /** Leading avatar/icon content (initials or an icon). */
  lead?: ReactNode;
  leadTone?: MobileLeadTone;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Trailing content (e.g. a MobileStatusPill or an amount). */
  trailing?: ReactNode;
  chevron?: boolean;
  onClick?: () => void;
}

export function MobileCardRow({ lead, leadTone = 'slate', title, subtitle, trailing, chevron = false, onClick }: MobileCardRowProps) {
  const interactive = typeof onClick === 'function';
  const content = (
    <>
      {lead != null && <span className={'m-lead ' + LEAD_TONE[leadTone]} aria-hidden>{lead}</span>}
      <span className="m-body">
        <span className="m-h">{title}</span>
        {subtitle != null && <span className="m-s">{subtitle}</span>}
      </span>
      {(trailing != null || chevron) && (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {trailing}
          {chevron && <span className="m-chev"><ChevronRight /></span>}
        </span>
      )}
    </>
  );
  return interactive
    ? <button type="button" className="m-row" onClick={onClick}>{content}</button>
    : <div className="m-row">{content}</div>;
}

// ── Detail row (46px) ───────────────────────────────────────────────────────
interface MobileDetailRowProps {
  label: string;
  value: ReactNode;
}

export function MobileDetailRow({ label, value }: MobileDetailRowProps) {
  return (
    <div className="m-drow">
      <span className="m-drow-k">{label}</span>
      <span className="m-drow-v">{value}</span>
    </div>
  );
}
